import { useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Banner, Button, Card, ErrorState, Skeleton, TextField } from '@shared/design-system/ui'
import { useSession } from '@shared/lib/auth/session-store'
import { hasAdminPermission } from '@shared/lib/auth/permissions'
import { getUserFacingError } from '@shared/lib/errors/user-facing-error'
import { applyApiFieldErrors } from '@shared/lib/forms/api-field-errors'
import { toast } from '@shared/lib/toast/toast-store'
import { PermissionGate } from '@shared/ui/PermissionGate'
import { updateFinancialQueryCache, useAdminFinancialQueryCache } from '../api/admin.api'
import * as s from './AdminFinancialCacheScreen.css'

export function AdminFinancialCacheScreen() {
  const user = useSession((state) => state.user)
  const canRead = hasAdminPermission(user, 'FINANCIAL_CACHE_READ')
  const canManage = hasAdminPermission(user, 'FINANCIAL_CACHE_MANAGE')
  const configuration = useAdminFinancialQueryCache(canRead)
  const queryClient = useQueryClient()
  const [draft, setDraft] = useState<string | null>(null)
  const [error, setError] = useState<string>()
  const input = useRef<HTMLInputElement>(null)
  const update = useMutation({
    mutationFn: updateFinancialQueryCache,
    onSuccess: (snapshot) => {
      queryClient.setQueryData(['admin-financial-query-cache'], snapshot)
      setDraft(null)
      setError(undefined)
      toast.success('Tempo de cache atualizado.')
    },
    onError: (failure) => {
      let hasFieldError = false
      applyApiFieldErrors(
        failure,
        (field, feedback) => {
          if (field === 'ttlSeconds') {
            hasFieldError = true
            setError(feedback.message)
            input.current?.focus()
          }
        },
        { ttlSeconds: 'ttlSeconds' },
      )
      if (!hasFieldError)
        toast.error(
          getUserFacingError(failure, {
            title: 'Não foi possível salvar o tempo de cache',
            fallback: 'Tente novamente. A configuração anterior foi mantida.',
          }),
        )
    },
  })

  return (
    <PermissionGate permission="FINANCIAL_CACHE_READ">
      <section className={s.page} data-testid="financial-cache-screen">
        <div className={s.header}>
          <h1 className={s.title}>Cache dos indicadores financeiros</h1>
          <p className={s.subtitle}>
            Ajuste por quanto tempo as consultas financeiras reutilizam os resultados calculados.
          </p>
        </div>
        {configuration.isLoading ? (
          <Skeleton height={240} />
        ) : configuration.isError || !configuration.data ? (
          <ErrorState
            title="Não foi possível carregar a configuração"
            description="Tente novamente para consultar o tempo de cache deste ambiente."
            onRetry={() => {
              void configuration.refetch()
            }}
          />
        ) : (
          <Card>
            <form
              className={s.form}
              data-testid="financial-cache-form"
              noValidate
              onSubmit={(event) => {
                event.preventDefault()
                if (!canManage || update.isPending) return
                const value = draft ?? String(configuration.data.ttlSeconds)
                const ttlSeconds = Number(value)
                if (
                  value.trim() === '' ||
                  !Number.isInteger(ttlSeconds) ||
                  ttlSeconds < 0 ||
                  ttlSeconds > 3600
                ) {
                  setError('Informe um número inteiro entre 0 e 3600 segundos.')
                  input.current?.focus()
                  return
                }
                setError(undefined)
                update.mutate({ ttlSeconds })
              }}
            >
              <p className={s.subtitle} data-testid="financial-cache-environment">
                Ambiente: <strong>{configuration.data.environment}</strong>
              </p>
              <Banner tone="info" title="Indicadores e operação financeira">
                Este prazo vale para todos os indicadores com cache, inclusive períodos históricos e
                saldo de exibição. Alterações nos dados aparecem após o prazo expirar e uma nova
                consulta. Saques, autorizações e demais operações financeiras continuam com leitura
                atual.
              </Banner>
              {error ? (
                <Banner tone="danger" title="Revise o tempo de cache">
                  Corrija o campo destacado antes de salvar.
                </Banner>
              ) : null}
              <TextField
                ref={input}
                label="Tempo de cache em segundos"
                type="number"
                min={0}
                max={3600}
                step={1}
                value={draft ?? String(configuration.data.ttlSeconds)}
                disabled={!canManage || update.isPending}
                error={error}
                helperText="Use de 0 a 3600 segundos. O valor 0 desabilita o cache neste ambiente. Aumentar o prazo pode atrasar a atualização dos indicadores."
                data-testid="financial-cache-ttl-input"
                onChange={(event) => {
                  setDraft(event.target.value)
                  setError(undefined)
                }}
              />
              {!canManage ? (
                <Banner tone="warning" title="Somente consulta">
                  Seu perfil pode consultar esta configuração. Solicite a alteração a um
                  administrador autorizado.
                </Banner>
              ) : (
                <Button
                  type="submit"
                  disabled={update.isPending}
                  data-testid="financial-cache-save-btn"
                >
                  {update.isPending ? 'Salvando...' : 'Salvar tempo de cache'}
                </Button>
              )}
            </form>
          </Card>
        )}
      </section>
    </PermissionGate>
  )
}
